/**
 * ESTILOS III: la ficha de tu voz, con el radar y los dos maestros (T-37).
 *
 * Tres honestidades que el Volumen 7 exige y que aquí se dibujan, no se explican en una nota al pie:
 *
 *  · **«No lo sé» se ve.** Un eje sin puntuar no se pinta en el polígono ni se rellena con la media:
 *    se queda como hueco y se dice. Un 6 inventado al lado de nueve juicios sería peor que el hueco.
 *  · **Los perfiles de los maestros son JUICIOS PUBLICADOS** (Apéndice A), no mediciones de sus
 *    libros. Van con otro trazo y con su aviso: sirven para situarte, no para darte una nota.
 *  · **Las medidas no son puntuaciones.** Van debajo, como evidencia de los ejes 1–4, nunca en su
 *    lugar.
 *
 * La ficha se puede rellenar con lo que ya dijo el informe de lectura del libro: el lector juzgó el
 * estilo capítulo a capítulo con el texto delante, y volver a puntuarlo aquí sería pagar dos veces
 * por el mismo juicio.
 */
import { useCallback, useEffect, useState } from "react";
import { anadirMuestraEstilo, autores, bibliotecaEstilos, crearFichaEstilo, fichaDesdeInforme, fichasDeEstilo } from "../api";
import { maestrosCerca, nombreDeRuta } from "../logica";
import type { Autor, EjeEstilo, FichaEstilo, Maestro } from "../api";

const FIABLE: Record<string, string> = {
  sin_datos: "Todavía sin puntuar",
  una_muestra: "Una sola lectura: aún no sabemos si es tu voz o ese pasaje",
  estable: "Dos lecturas de acuerdo: esto es tu voz",
  pasaje: "Dos lecturas que no coinciden: has puntuado un pasaje, no a un escritor",
};

export default function Estilos({ carpeta, doc, alHablar }: { carpeta: string; doc: string | null; alHablar?: (agente: string, encargo: string) => void }) {
  const [firmas, setFirmas] = useState<Autor[] | null>(null);
  const [quien, setQuien] = useState<string | null>(null);
  const [ejes, setEjes] = useState<EjeEstilo[]>([]);
  const [maestros, setMaestros] = useState<Maestro[]>([]);
  const [fichas, setFichas] = useState<FichaEstilo[] | null>(null);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [nueva, setNueva] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    autores().then((r) => {
      setFirmas(r.autores);
      setQuien((q) => q ?? r.autores[0]?.id ?? null);
    }).catch(() => setFirmas([]));
    bibliotecaEstilos().then((r) => { setEjes(r.ejes); setMaestros(r.biblioteca); }).catch(() => {});
  }, []);

  const cargar = useCallback(() => {
    if (!quien) { setFichas(null); return; }
    fichasDeEstilo(quien).then((r) => {
      setFichas(r.fichas);
      setAbierta((a) => (r.fichas.some((f) => f.id === a) ? a : r.fichas[0]?.id ?? null));
    }).catch(() => setFichas([]));
  }, [quien]);
  useEffect(cargar, [cargar]);

  const ficha = fichas?.find((f) => f.id === abierta) ?? null;
  const ultima = ficha?.puntuaciones?.at(-1) ?? null;

  async function hacer(fn: () => Promise<unknown>, dicho?: string) {
    setOcupado(true); setFallo(null); setAviso(null);
    try { await fn(); if (dicho) setAviso(dicho); cargar(); }
    catch (e) { setFallo((e as Error).message); }
    finally { setOcupado(false); }
  }

  if (firmas && !firmas.length) {
    return <section className="carta"><h2>Estilos</h2>
      <p className="vacio">Una ficha de estilo es de una firma, así que primero crea la tuya en Autores.</p></section>;
  }

  return (
    <section className="carta">
      <h2>Estilos <span className="sub">Los diez ejes de tu voz, y con quién se parece</span></h2>

      <div className="botones">
        <label className="campo" style={{ margin: 0 }}>
          <span>Firma</span>
          <div className="dir">
            <select className="escribir" value={quien ?? ""} onChange={(e) => setQuien(e.target.value)}>
              {(firmas ?? []).map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          </div>
        </label>
      </div>

      {/* Rais, 21-sep: «no se puede crear la ficha; ¿cómo trabajo el estilo?». La caja vacía con
          «thriller, ensayo, terror…» y un botón apagado no decía qué hacer. Sin fichas, un solo botón
          que lo hace todo: crea la ficha de este libro y trae los diez ejes del informe de lectura. */}
      {fichas && !fichas.length && (
        <div className="aviso">
          <p style={{ margin: "0 0 6px" }}><b>Cómo se trabaja el estilo, en tres pasos</b></p>
          <ol className="vacio" style={{ margin: "0 0 10px 18px", padding: 0 }}>
            <li><b>La ficha de tu voz</b>: los diez ejes que el lector ya juzgó en tu informe de lectura, con el radar y los dos autores a los que más te pareces.</li>
            <li><b>Muestras</b>: pasajes tuyos de unas 500 palabras; con dos que coinciden sabemos que es tu voz y no un pasaje.</li>
            <li><b>Trabajarlo con el coach</b>: qué conservar y qué vigilar. El editor lee la ficha, la guía y esa conversación antes de reescribir nada.</li>
          </ol>
          <div className="botones">
            <button className="btn" disabled={!quien || !carpeta || ocupado}
                    onClick={() => hacer(async () => {
                      const nombre = doc ? nombreDeRuta(doc).replace(/\.docx$/i, "").replace(/-m\d+-v\d+$/i, "") : "Mi voz";
                      const f = await crearFichaEstilo(quien!, nombre);
                      setAbierta(f.id);
                      try {
                        const r = await fichaDesdeInforme(quien!, f.id, carpeta, doc);
                        setAviso(`Ficha «${nombre}» creada con los diez ejes del ${r.puntuacion.origen}.`
                          + (r.referencias?.length ? ` Te compara con ${r.referencias.map((x) => x.autor).join(" y ")}.` : ""));
                      } catch (e) {
                        setAviso(`Ficha «${nombre}» creada, todavía sin puntuar: ${(e as Error).message} Pide el informe de lectura en Diagnóstico y vuelve a «Traer los ejes del informe de lectura».`);
                      }
                    })}>
              {ocupado ? "Creándola…" : "Crear la ficha de este libro con el informe de lectura"}
            </button>
          </div>
        </div>
      )}

      <div className="botones">
        {(fichas ?? []).map((f) => (
          <button key={f.id} className={"chip" + (f.id === abierta ? " puesto" : "")} onClick={() => setAbierta(f.id)}>
            {f.nombre}
          </button>
        ))}
        <input className="escribir" style={{ maxWidth: 240 }} value={nueva} aria-label="Nombre de otra ficha" placeholder="Otra ficha: escribe su nombre (terror, ensayo…)"
               onChange={(e) => setNueva(e.target.value)} />
        <button className="btn fantasma" disabled={!nueva.trim() || !quien || ocupado}
                onClick={() => hacer(async () => { await crearFichaEstilo(quien!, nueva.trim()); setNueva(""); })}>
          Crear ficha
        </button>
      </div>

      {ficha && (
        <>
          <p className="vacio">
            {ficha.muestras.length} {ficha.muestras.length === 1 ? "muestra" : "muestras"} · {FIABLE[ficha.fiabilidad.veredicto]}
            {ficha.fiabilidad.ejesDispares.length
              ? ` (${ficha.fiabilidad.ejesDispares.map((e) => e.id).join(", ")}: más de dos puntos de diferencia)`
              : ""}
          </p>

          {ultima ? (
            <Radar ejes={ejes} mios={ultima.ejes} maestros={maestros} />
          ) : (
            <p className="vacio">
              Esta ficha todavía no está puntuada. Si ya tienes el informe de lectura del libro, puedes
              traer de ahí los diez ejes: el lector los juzgó con el texto delante.
            </p>
          )}

          <div className="botones">
            <button className="btn" disabled={!carpeta || !quien || ocupado}
                    onClick={() => hacer(async () => {
                      const r = await fichaDesdeInforme(quien!, ficha.id, carpeta, doc);
                      setAviso(`Traídos los diez ejes del ${r.puntuacion.origen}.`
                        + (r.referencias?.length ? ` Te compara con ${r.referencias.map((x) => x.autor).join(" y ")}.` : ""));
                    })}>
              Traer los ejes del informe de lectura
            </button>
            {alHablar && (
              <button className="btn fantasma" onClick={() => alHablar("coach", "Quiero trabajar mi estilo. Mira la ficha de estilo de mi libro (los diez ejes) y dime qué tengo que conservar, qué vigilar y qué no tocar en la reescritura. Hazme después la guía de estilo.")}>
                Trabajar el estilo con el coach
              </button>
            )}
            <MuestraNueva autor={quien!} ficha={ficha.id} ocupado={ocupado}
                          alHecho={(palabras) => hacer(async () => {}, `Muestra guardada: ${palabras} palabras.`)} />
          </div>

          {ultima && (
            <div className="notas">
              {ejes.map((e) => {
                const x = ultima.ejes[e.id];
                const n = x?.puntuacion ?? null;
                return (
                  <div key={e.id}>
                    <div className="nota">
                      <span className="et">{e.n}. {corto(e.id, e.nombre)}</span>
                      <b>{n ?? "—"}</b>
                      <span className="raya">
                        {n != null && <i style={{ width: `${n * 10}%`, background: "var(--azul)" }} />}
                      </span>
                    </div>
                    <p className="vacio" style={{ margin: "2px 0 0 108px" }}>
                      {n == null ? "No lo sé — y decirlo vale más que un número inventado." : x?.justificacion}
                    </p>
                  </div>
                );
              })}
              <p className="vacio">De {ultima.origen}, {String(ultima.fecha).slice(0, 10)}.</p>
            </div>
          )}
        </>
      )}

      {aviso && <p className="vacio">{aviso}</p>}
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
    </section>
  );
}

/** Subir una muestra: prosa tuya, que se queda en tu disco. */
function MuestraNueva({ autor, ficha, ocupado, alHecho }: {
  autor: string; ficha: string; ocupado: boolean; alHecho: (palabras: number) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState("");
  const palabras = texto.trim() ? texto.trim().split(/\s+/).length : 0;
  if (!abierto) return <button className="btn fantasma" onClick={() => setAbierto(true)}>Añadir una muestra</button>;
  return (
    <div className="formulario" style={{ maxWidth: "none", width: "100%" }}>
      <label className="campo"><span>Pega un pasaje tuyo <i>(el libro pide unas 500 palabras; no sale de tu disco)</i></span>
        <div className="dir">
          <textarea className="escribir" rows={6} value={texto} onChange={(e) => setTexto(e.target.value)} />
        </div>
      </label>
      <p className="vacio">{palabras} palabras{palabras && palabras < 500 ? " · con menos de 500 la lectura mide el pasaje, no tu voz" : ""}</p>
      <div className="botones">
        <button className="btn" disabled={palabras < 50 || ocupado} onClick={async () => {
          await anadirMuestraEstilo(autor, ficha, texto.trim(), "pegada en el Studio");
          alHecho(palabras); setTexto(""); setAbierto(false);
        }}>Guardar la muestra</button>
        <button className="btn texto" onClick={() => setAbierto(false)}>Cancelar</button>
      </div>
    </div>
  );
}

// ── El radar ────────────────────────────────────────────────────────────────────────────────────
const W = 460, H = 380, CX = W / 2, CY = H / 2 + 6, R = 122;

/**
 * El nombre del eje, corto, para que quepa en el radar sin cortarse a media palabra. La primera
 * palabra vale para casi todos; dos se quedaban en una palabra que no dice nada («Densidad»,
 * «Voz»), así que esos dos llevan su rótulo. La lista sigue viniendo del motor: un eje que no
 * esté aquí se acorta con la regla y se pinta igual.
 */
const ROTULO: Record<string, string> = {
  subtexto: "Subtexto", distancia: "Voz clínica", alternancia: "Alternancia", contencion: "Contención",
};
const corto = (id: string, nombre: string) => ROTULO[id] ?? nombre.split(/ de | y |\//)[0].trim();

const punto = (i: number, total: number, v: number) => {
  const a = -Math.PI / 2 + (2 * Math.PI * i) / total;
  const r = (R * v) / 10;
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)] as const;
};

export function Radar({ ejes, mios, maestros }: {
  ejes: EjeEstilo[]; mios: Record<string, { puntuacion: number | null; justificacion: string }>; maestros: Maestro[];
}) {
  const cerca = maestrosCerca(mios, maestros);
  const total = ejes.length || 10;
  const puestos = ejes.map((e, i) => ({ e, i, v: mios[e.id]?.puntuacion ?? null }));
  const sinPuntuar = puestos.filter((p) => p.v == null);
  // El polígono salta los ejes sin puntuar: rellenarlos con la media dibujaría una voz que nadie ha
  // juzgado. Con menos de tres ejes no hay figura que valga, y se dice.
  const camino = (valores: (number | null)[]) => {
    const ps = valores.map((v, i) => (v == null ? null : punto(i, total, v))).filter(Boolean) as (readonly [number, number])[];
    return ps.length >= 3 ? ps.map((p, k) => `${k ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ") + " Z" : "";
  };
  const rotulo = `Radar de los diez ejes. ${puestos.filter((p) => p.v != null).length} puntuados`
    + (sinPuntuar.length ? `, ${sinPuntuar.length} sin puntuar` : "")
    + (cerca.length ? `. Comparado con ${cerca.map((m) => m.nombre).join(" y ")}.` : "");

  return (
    <div className="plano">
      <div className="grafico">
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={rotulo}>
          {[2, 4, 6, 8, 10].map((v) => (
            <polygon key={v} fill="none" stroke="var(--linea)" strokeWidth={v === 10 ? 1 : 0.5}
                     points={ejes.map((_, i) => punto(i, total, v).join(",")).join(" ")} />
          ))}
          {ejes.map((e, i) => {
            const [x, y] = punto(i, total, 10);
            const [tx, ty] = punto(i, total, 11.9);
            return (
              <g key={e.id}>
                <line x1={CX} y1={CY} x2={x} y2={y} stroke="var(--linea)" strokeWidth={0.5} />
                <text className="eje" x={tx} y={ty} textAnchor={Math.abs(tx - CX) < 6 ? "middle" : tx > CX ? "start" : "end"}
                      dominantBaseline="middle" fill={mios[e.id]?.puntuacion == null ? "var(--mut)" : "var(--ink)"}>
                  {corto(e.id, e.nombre)}{mios[e.id]?.puntuacion == null ? " ·" : ""}
                </text>
              </g>
            );
          })}
          {cerca.map((m, k) => (
            <path key={m.id} d={camino(ejes.map((e) => m.huella[e.id] ?? null))} fill="none"
                  stroke="var(--mut)" strokeWidth={1} strokeDasharray={k ? "2 3" : "5 3"} opacity={0.75} />
          ))}
          {/* Cian: en esta casa el cian es lo humano, y esto es la voz del autor. El oro es de la
              máquina y del oficio, y aquí no pinta nada. */}
          <path d={camino(puestos.map((p) => p.v))} fill="var(--azul)" fillOpacity={0.16}
                stroke="var(--azul)" strokeWidth={1.8} />
          {sinPuntuar.map((p) => {
            const [x, y] = punto(p.i, total, 10);
            return <circle key={"hueco-" + p.e.id} cx={x} cy={y} r={3} fill="var(--sup2)" stroke="var(--mut)"
                           strokeWidth={0.8} strokeDasharray="1.5 1.5" />;
          })}
          {puestos.filter((p) => p.v != null).map((p) => {
            const [x, y] = punto(p.i, total, p.v as number);
            return <circle key={p.e.id} cx={x} cy={y} r={2.6} fill="var(--azul)" />;
          })}
        </svg>
      </div>
      <div>
        <p><b>Tu voz</b>, en cian.</p>
        {cerca.length ? (
          <>
            <p className="vacio">
              Se parece a <b>{cerca[0].nombre}</b> (línea larga){cerca[1] ? <> y a <b>{cerca[1].nombre}</b> (punteada)</> : null}.
            </p>
            <p className="vacio">
              Sus perfiles son los juicios publicados del Volumen 7, no medidas de sus libros: sirven para
              situarte, no para ponerte nota.{cerca.some((m) => m.provisional) ? " El del autor marcado como provisional lo dice el propio libro." : ""}
            </p>
          </>
        ) : (
          <p className="vacio">Con menos de tres ejes puntuados no se compara con nadie: saldría un parecido inventado.</p>
        )}
        {sinPuntuar.length > 0 && (
          <p className="vacio">
            Sin puntuar: {sinPuntuar.map((p) => p.e.nombre).join(", ")}. El hueco se deja a propósito —
            «no lo sé» es información; un 6 inventado, no.
          </p>
        )}
      </div>
    </div>
  );
}
